import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import HomeRpgLogoMark from "../home/rpg/HomeRpgLogoMark";
import TreydoraNavAuth from "../Header/TreydoraNavAuth";
import whitepaperPdf from "../../lib/whitepaperPdf";
import { useAuth } from "../../context/AuthContext";
import {
  SITE_DASHBOARD_PATH,
  SITE_HOME_PATH,
  SITE_MARKETPLACE_PATH,
  SITE_NAV_ROUTES,
  SITE_OSHAR_REAL_ESTATE_PATH,
  SITE_PUBLIC_LIBRARY_PATH,
  SITE_WORLD_PATH,
} from "../../constants/siteNavRoutes";
import "../Header/header-treydora-premium.css";
import "./treydora-header.css";

const HEADER_DRAWER_BREAKPOINT_PX = 1024;
const MOBILE_NAV_MQ = `(max-width: ${HEADER_DRAWER_BREAKPOINT_PX}px)`;

function topLinkClass(active) {
  return `th-topnav__link${active ? " is-active" : ""}`;
}

function useMobileHeaderNav() {
  const [mobile, setMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(MOBILE_NAV_MQ).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_NAV_MQ);
    const sync = () => setMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return mobile;
}

const NAV_ITEMS = [
  { key: "world", label: "World", to: SITE_WORLD_PATH, end: true },
  { key: "oshar-re", label: "Oshar Real Estate", to: SITE_OSHAR_REAL_ESTATE_PATH },
  { key: "chronicles", label: "Chronicles", to: "/chronicles" },
  { key: "library", label: "Library", to: SITE_PUBLIC_LIBRARY_PATH, end: true },
  { key: "marketplace", label: "Marketplace", to: SITE_MARKETPLACE_PATH },
  {
    key: "dashboard",
    label: "Dashboard",
    to: SITE_DASHBOARD_PATH,
    end: true,
    requiresAuth: true,
  },
  {
    key: "whitepaper",
    label: "Whitepaper",
    href: whitepaperPdf,
    download: "treydorawhitepaper",
    quiet: true,
  },
];

export default function TreydoraHeader() {
  const { pathname, hash } = useLocation();
  const { isAuthenticated } = useAuth();
  const isMobileNav = useMobileHeaderNav();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);
  const visibleNavItems = useMemo(
    () =>
      NAV_ITEMS.filter((item) => {
        if (!item.requiresAuth || isAuthenticated) {
          // Signed-in desktop: Dashboard lives in TreydoraNavAuth (right rail), not center nav.
          if (item.requiresAuth && isAuthenticated && !isMobileNav) {
            return false;
          }
          return true;
        }
        return false;
      }),
    [isAuthenticated, isMobileNav]
  );

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname, hash]);

  useEffect(() => {
    if (!isMobileNav) {
      setDrawerOpen(false);
    }
  }, [isMobileNav]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  useEffect(() => {
    if (!isMobileNav || !drawerOpen) {
      document.body.classList.remove("th-drawer-open");
      return undefined;
    }
    document.body.classList.add("th-drawer-open");
    return () => document.body.classList.remove("th-drawer-open");
  }, [drawerOpen, isMobileNav]);

  const osharReActive = pathname.startsWith("/world/oshar/real-estate");
  const worldActive =
    (pathname === "/world" || pathname.startsWith("/world/")) && !osharReActive;
  const chroniclesActive = pathname.startsWith("/chronicles");
  const libraryActive = pathname === "/library";
  const marketplaceActive = pathname.startsWith("/marketplace");
  const dashboardActive =
    pathname === SITE_DASHBOARD_PATH || pathname.startsWith(`${SITE_DASHBOARD_PATH}/`);
  const whitepaperActive = pathname === "/whitepaper";

  const isItemActive = (item) => {
    switch (item.key) {
      case "world":
        return worldActive;
      case "oshar-re":
        return osharReActive;
      case "chronicles":
        return chroniclesActive;
      case "library":
        return libraryActive;
      case "marketplace":
        return marketplaceActive;
      case "dashboard":
        return dashboardActive;
      case "whitepaper":
        return whitepaperActive;
      default:
        return false;
    }
  };

  const renderNavItem = (item, onNavigate) => {
    const className = `${topLinkClass(isItemActive(item))}${item.quiet ? " th-topnav__link--quiet" : ""}`;

    if (item.href) {
      return (
        <a
          key={item.key}
          href={item.href}
          download={item.download}
          className={className}
          onClick={onNavigate}
        >
          {item.label}
        </a>
      );
    }

    if (item.hash) {
      return (
        <HashLink smooth key={item.key} to={item.to} className={className} onClick={onNavigate}>
          {item.label}
        </HashLink>
      );
    }

    if (item.requiresAuth) {
      return (
        <NavLink
          key={item.key}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) => topLinkClass(isActive)}
        >
          {item.label}
        </NavLink>
      );
    }

    return (
      <NavLink key={item.key} to={item.to} end={item.end} onClick={onNavigate} className={className}>
        {item.label}
      </NavLink>
    );
  };

  return (
    <header className={`th-header${drawerOpen && isMobileNav ? " th-header--drawer-open" : ""}`} id="header" role="banner">
      <div className="th-header__bar">
        <div className="th-topnav__inner">
          {isMobileNav ? (
            <button
              type="button"
              className="th-topnav__burger mobile-menu-toggle"
              aria-expanded={drawerOpen}
              aria-controls="th-site-drawer"
              aria-label={drawerOpen ? "Close menu" : "Open menu"}
              onClick={() => setDrawerOpen((open) => !open)}
            >
              <i className={`fas ${drawerOpen ? "fa-times" : "fa-bars"}`} aria-hidden />
            </button>
          ) : null}

          <Link to={SITE_HOME_PATH} className="th-topnav__logo" onClick={closeDrawer} aria-label="Treydora home">
            <HomeRpgLogoMark />
          </Link>

          {!isMobileNav ? (
            <nav className="th-topnav__center site-nav desktop-nav" aria-label="Treydora">
              {visibleNavItems.map((item) => renderNavItem(item, closeDrawer))}
            </nav>
          ) : null}

          {!isMobileNav ? (
            <div className="th-topnav__right th-header__actions">
              <HashLink smooth to={SITE_NAV_ROUTES.TOKEN_SALE} className="th-topnav__trdx" onClick={closeDrawer}>
                Get TRDX
              </HashLink>
              <div className="th-topnav__identity-slot th-header__auth">
                <TreydoraNavAuth className="th-topnav__auth td-site-header__auth" placement="desktop" />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {isMobileNav ? (
        <>
          <button
            type="button"
            className={`th-drawer-backdrop${drawerOpen ? " is-visible" : ""}`}
            aria-label="Close menu"
            aria-hidden={!drawerOpen}
            tabIndex={drawerOpen ? 0 : -1}
            onClick={closeDrawer}
          />
          <div
            className={`th-drawer site-nav${drawerOpen ? " is-open" : ""}`}
            id="th-site-drawer"
            aria-hidden={!drawerOpen}
          >
            <nav className="th-drawer__nav" aria-label="Site">
              {visibleNavItems.map((item) => renderNavItem(item, closeDrawer))}
            </nav>
            <HashLink
              smooth
              to={SITE_NAV_ROUTES.TOKEN_SALE}
              className="th-topnav__trdx th-topnav__trdx--block"
              onClick={closeDrawer}
            >
              Get TRDX
            </HashLink>
            <div className="th-drawer__auth">
              <TreydoraNavAuth className="th-topnav__auth td-site-header__auth th-drawer__auth-inner" placement="drawer" />
            </div>
          </div>
        </>
      ) : null}
    </header>
  );
}
